import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { supabase, User } from '../lib/supabase'
import type { Provider } from '@supabase/supabase-js'
import { syncCoreMissions } from '../lib/missionService'

// Storage key constants
const AUTH_STORAGE_KEY = 'bedrock-auth-storage'
const SESSION_CHECK_KEY = 'bedrock-session-check'

interface AuthState {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  isInitialized: boolean
  lastAuthCheck: number | null
  authError: string | null
  
  // Actions
  signUp: (email: string, password: string, username: string) => Promise<{ success: boolean; error?: string }>
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  signInWithProvider: (provider: 'discord' | 'google') => Promise<{ success: boolean; error?: string }>
  signOut: () => Promise<void>
  fetchUser: () => Promise<void>
  updateUser: (updates: Partial<User>) => Promise<{ success: boolean; error?: string }>
  initializeAuth: () => Promise<void>
  validateSession: () => Promise<boolean>
  clearError: () => void
}

// Logger utility
const logger = {
  info: (message: string, data?: any) => {
    console.log(`[AUTH INFO] ${new Date().toISOString()}: ${message}`, data || '')
  },
  error: (message: string, data?: any) => {
    console.error(`[AUTH ERROR] ${new Date().toISOString()}: ${message}`, data || '')
  },
  warn: (message: string, data?: any) => {
    console.warn(`[AUTH WARN] ${new Date().toISOString()}: ${message}`, data || '')
  }
}

// Create the auth store with improved session handling
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,
      isAuthenticated: false,
      isInitialized: false,
      lastAuthCheck: null,
      authError: null,

      clearError: () => set({ authError: null }),

      initializeAuth: async () => {
        logger.info('Initializing auth...')
        
        set({ isLoading: true, authError: null })
        
        try {
          // Check for existing session
          const { data: { session }, error: sessionError } = await supabase.auth.getSession()
          
          if (sessionError) {
            logger.error('Error getting session', sessionError)
            set({ 
              isLoading: false, 
              isInitialized: true, 
              authError: sessionError.message 
            })
            return
          }
          
          if (session) {
            logger.info('Session found, fetching user data')
            // Fetch user data from database
            await get().fetchUser()
          } else {
            logger.info('No session found')
            set({ 
              user: null, 
              isAuthenticated: false, 
              isLoading: false, 
              isInitialized: true,
              lastAuthCheck: Date.now()
            })
          }
          
          // Set up auth state change listener
          setupAuthListener()
          
        } catch (error: any) {
          logger.error('Auth initialization error', error)
          set({ 
            isLoading: false, 
            isInitialized: true, 
            authError: error.message || 'Error al inicializar autenticación'
          })
        }
      },

      validateSession: async () => {
        logger.info('Validating session...')
        
        try {
          const { data: { session }, error } = await supabase.auth.getSession()
          
          if (error) {
            logger.error('Session validation error', error)
            set({ 
              isAuthenticated: false, 
              user: null,
              lastAuthCheck: Date.now(),
              authError: error.message 
            })
            return false
          }
          
          if (!session) {
            logger.info('No session during validation')
            set({ 
              isAuthenticated: false, 
              user: null,
              lastAuthCheck: Date.now()
            })
            return false
          }
          
          // Check if token is expired
          const expiresAt = session.expires_at ? session.expires_at * 1000 : 0
          const now = Date.now()
          
          if (expiresAt && expiresAt < now) {
            logger.info('Token expired, attempting refresh')
            
            const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession()
            
            if (refreshError || !refreshedSession) {
              logger.error('Token refresh failed', refreshError)
              set({ 
                isAuthenticated: false, 
                user: null,
                lastAuthCheck: Date.now(),
                authError: 'Sesión expirada. Por favor, inicia sesión nuevamente.'
              })
              return false
            }
            
            logger.info('Token refreshed successfully')
          }
          
          // Update last check time
          set({ lastAuthCheck: Date.now() })
          
          // Fetch user data to ensure it's current
          await get().fetchUser()
          
          return get().isAuthenticated
          
        } catch (error: any) {
          logger.error('Session validation exception', error)
          set({ 
            isAuthenticated: false, 
            user: null,
            lastAuthCheck: Date.now(),
            authError: error.message 
          })
          return false
        }
      },

      signUp: async (email: string, password: string, username: string) => {
        logger.info('Signing up user', { email, username })
        set({ isLoading: true, authError: null })
        
        try {
          // Check if username already exists
          const { data: existingUser } = await supabase
            .from('users')
            .select('id')
            .eq('username', username.toLowerCase())
            .single()
          
          if (existingUser) {
            logger.warn('Username already taken', { username })
            set({ isLoading: false })
            return { success: false, error: 'El nombre de usuario ya está en uso' }
          }

          // Check if email already exists in our users table
          const { data: existingEmail } = await supabase
            .from('users')
            .select('id')
            .eq('email', email)
            .single()
          
          if (existingEmail) {
            logger.warn('Email already registered', { email })
            set({ isLoading: false })
            return { success: false, error: 'Ya existe una cuenta con este email' }
          }

          // Sign up with Supabase Auth
          const { data: authData, error: authError } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                username,
              }
            }
          })

          if (authError) {
            logger.error('Supabase signup error', authError)
            set({ isLoading: false, authError: authError.message })
            return { success: false, error: authError.message }
          }

          if (!authData.user) {
            logger.error('No user data after signup')
            set({ isLoading: false })
            return { success: false, error: 'Error al crear usuario' }
          }

          logger.info('User created in auth', { userId: authData.user.id })

          // Generate referral code
          const referralCode = `${username.toLowerCase()}_${Date.now().toString(36)}`

          // Create user profile in database
          const { error: profileError } = await supabase
            .from('users')
            .insert({
              id: authData.user.id,
              email,
              username: username.toLowerCase(),
              referral_code: referralCode,
              points: 0,
              xp: 0,
              level: 1,
              plan: 'FREE',
            })

          if (profileError) {
            logger.error('Profile creation error', profileError)
            set({ isLoading: false, authError: profileError.message })
            return { success: false, error: profileError.message }
          }

          logger.info('Profile created successfully')
          
          // Fetch the created user
          await get().fetchUser()
          
          set({ isLoading: false })
          logger.info('Signup completed successfully')
          return { success: true }
          
        } catch (error: any) {
          logger.error('Signup exception', error)
          set({ isLoading: false, authError: error.message })
          return { success: false, error: error.message || 'Error inesperado' }
        }
      },

      signIn: async (email: string, password: string) => {
        logger.info('Signing in user', { email })
        set({ isLoading: true, authError: null })
        
        try {
          const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
            email,
            password,
          })

          if (authError) {
            logger.error('Login error', authError)
            set({ isLoading: false, authError: authError.message })
            return { success: false, error: authError.message }
          }

          if (!authData.user) {
            logger.error('No user after login')
            set({ isLoading: false })
            return { success: false, error: 'Error al iniciar sesión' }
          }

          logger.info('User logged in', { userId: authData.user.id })
          
          // Fetch user data from database
          await get().fetchUser()
          
          set({ isLoading: false })
          logger.info('Login completed successfully')
          return { success: true }
          
        } catch (error: any) {
          logger.error('Login exception', error)
          set({ isLoading: false, authError: error.message })
          return { success: false, error: error.message || 'Error inesperado' }
        }
      },

      signInWithProvider: async (provider: 'discord' | 'google') => {
        logger.info('Signing in with provider', { provider })
        set({ isLoading: true, authError: null })
        
        try {
          const { data, error } = await supabase.auth.signInWithOAuth({
            provider,
            options: {
              redirectTo: `${window.location.origin}/auth/callback`,
              scopes: provider === 'discord' ? 'identify email' : undefined,
            }
          })
          
          if (error) {
            logger.error('OAuth error', error)
            set({ isLoading: false, authError: error.message })
            return { success: false, error: error.message }
          }
          
          // OAuth redirects, so we just return success
          set({ isLoading: false })
          return { success: true }
          
        } catch (error: any) {
          logger.error('OAuth exception', error)
          set({ isLoading: false, authError: error.message })
          return { success: false, error: error.message || 'Error inesperado' }
        }
      },

      signOut: async () => {
        logger.info('Signing out user')
        
        try {
          await supabase.auth.signOut()
          logger.info('Signed out from Supabase')
        } catch (error) {
          logger.error('Signout error', error)
        }
        
        set({ user: null, isAuthenticated: false, authError: null })
        logger.info('Local state cleared')
      },

      fetchUser: async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession()
          
          if (!session?.user) {
            logger.info('No session in fetchUser')
            set({ user: null, isAuthenticated: false, isInitialized: true })
            return
          }

          logger.info('Fetching user from database', { userId: session.user.id })

          const { data: user, error } = await supabase
            .from('users')
            .select('*')
            .eq('id', session.user.id)
            .single()

          if (error) {
            logger.error('Error fetching user', error)
            
            // If user profile doesn't exist, create one
            if (error.code === 'PGRST116') {
              logger.info('Creating missing user profile')
              
              const referralCode = `${session.user.email?.split('@')[0] || 'user'}_${Date.now().toString(36)}`
              
              await supabase.from('users').insert({
                id: session.user.id,
                email: session.user.email,
                username: session.user.email?.split('@')[0] || 'user',
                referral_code: referralCode,
                points: 0,
                xp: 0,
                level: 1,
                plan: 'FREE',
              })
              
              // Try fetching again
              const { data: newUser } = await supabase
                .from('users')
                .select('*')
                .eq('id', session.user.id)
                .single()
              
              if (newUser) {
                set({ user: newUser, isAuthenticated: true, isInitialized: true })
                logger.info('User profile created and set')
                return
              }
            }
            
            set({ user: null, isAuthenticated: false, isInitialized: true })
            return
          }

          set({ user, isAuthenticated: true, isInitialized: true })
          logger.info('User fetched and set', { userId: user.id })

          // Keep baseline missions in sync for all users (first login + profile completion)
          void syncCoreMissions(user)
          
        } catch (error: any) {
          logger.error('FetchUser exception', error)
          set({ user: null, isAuthenticated: false, isInitialized: true, authError: error.message })
        }
      },

      updateUser: async (updates: Partial<User>) => {
        const { user } = get()
        if (!user) {
          return { success: false, error: 'No hay usuario autenticado' }
        }

        try {
          const pendingUpdates: Record<string, unknown> = { ...updates }
          const droppedColumns: string[] = []

          // Retry automatically when the DB schema is missing optional columns.
          while (Object.keys(pendingUpdates).length > 0) {
            const { error } = await supabase
              .from('users')
              .update(pendingUpdates)
              .eq('id', user.id)

            if (!error) {
              set({ user: { ...user, ...(pendingUpdates as Partial<User>) } })
              logger.info('User updated', { updates: pendingUpdates, droppedColumns })
              const latestUser = { ...user, ...(pendingUpdates as Partial<User>) } as User
              void syncCoreMissions(latestUser)
              return { success: true }
            }

            const message = error.message || ''
            const missingColMatch = message.match(/Could not find the '([^']+)' column/i)
            const missingColumn = missingColMatch?.[1]

            if (missingColumn && missingColumn in pendingUpdates) {
              droppedColumns.push(missingColumn)
              delete pendingUpdates[missingColumn]
              logger.warn('Skipping missing DB column during update', { missingColumn, message })
              continue
            }

            logger.error('Update user error', error)
            return { success: false, error: error.message }
          }

          return { success: false, error: 'No se pudieron guardar campos compatibles con tu esquema actual.' }
        } catch (error: any) {
          logger.error('UpdateUser exception', error)
          return { success: false, error: error.message }
        }
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        lastAuthCheck: state.lastAuthCheck
      }),
      onRehydrateStorage: () => (state) => {
        logger.info('Auth state rehydrated', { 
          hasUser: !!state?.user,
          isAuthenticated: state?.isAuthenticated 
        })
      }
    }
  )
)

// Setup auth state change listener
function setupAuthListener() {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
    logger.info('Auth state changed', { event, hasSession: !!session })
    
    const store = useAuthStore.getState()
    
    switch (event) {
      case 'SIGNED_IN':
        if (session) {
          logger.info('User signed in, fetching user data')
          await useAuthStore.getState().fetchUser()
        }
        break
        
      case 'SIGNED_OUT':
        logger.info('User signed out')
        useAuthStore.setState({ 
          user: null, 
          isAuthenticated: false,
          authError: null
        })
        break
        
      case 'TOKEN_REFRESHED':
        logger.info('Token refreshed')
        if (session) {
          await useAuthStore.getState().fetchUser()
        }
        break
        
      case 'USER_UPDATED':
        logger.info('User updated')
        if (session) {
          await useAuthStore.getState().fetchUser()
        }
        break
        
      case 'PASSWORD_RECOVERY':
        logger.info('Password recovery')
        break
        
      default:
        logger.info('Unhandled auth event', event)
    }
  })
  
  // Store subscription for cleanup (can be called from app)
  if (typeof window !== 'undefined') {
    (window as any).__authSubscription = subscription
  }
}

// Auto-initialize on import (will be called from App.tsx)
export const initializeAuthOnAppStart = async () => {
  const store = useAuthStore.getState()
  
  // If already initialized, validate session
  if (store.isInitialized) {
    logger.info('Already initialized, validating session')
    await store.validateSession()
    return
  }
  
  logger.info('First time initialization')
  await store.initializeAuth()
}

// Check and recover session periodically
export const startSessionRecovery = () => {
  // Check session every 5 minutes
  const interval = setInterval(async () => {
    const store = useAuthStore.getState()
    
    if (store.isAuthenticated) {
      logger.info('Periodic session check')
      await store.validateSession()
    }
  }, 5 * 60 * 1000)
  
  // Cleanup on unmount
  if (typeof window !== 'undefined') {
    (window as any).__sessionRecoveryInterval = interval
  }
}

export default useAuthStore
